import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    Optional,
    Output,
    SimpleChanges
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    Validator
} from '@angular/forms';
import {
    MissionCrewDto,
    MissionDetailsDto,
    MissionRoleDto
} from '@flyfreely-portal-ui/flyfreely';
import { FormatPersonPipe, personSearch } from '@flyfreely-portal-ui/ui';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import {
    MissionEditService,
    PersonWithRegisterStatus
} from '../mission-edit-v2/mission-edit.service';
import { MissionPilotRoleDataService } from './mission-pilot-role-data.service';

export const MISSION_PILOT_ROLEV2_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MissionPilotRoleV2),
    multi: true
};

export const MISSION_PILOT_ROLEV2_VALIDATOR: any = {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => MissionPilotRoleV2),
    multi: true
};

@Component({
    selector: '[mission-pilot-role-v2]',
    templateUrl: './mission-pilot-role-v2.component.html',
    providers: [
        MISSION_PILOT_ROLEV2_ACCESSOR,
        MISSION_PILOT_ROLEV2_VALIDATOR,
        MissionPilotRoleDataService
    ]
})
export class MissionPilotRoleV2 implements ControlValueAccessor, Validator {
    @Input() roles: MissionRoleDto[];
    @Input() disable: boolean;
    @Input() isLoading: boolean;
    @Input() readonly: boolean;
    @Input() missionDetails: MissionDetailsDto;
    @Input() pilotRegisterDisabled = true;
    @Input() orgId: number;
    @Input() showStatus = true;

    @Output() deleteCrew = new EventEmitter<void>();
    @Output() toggleShowDetails = new EventEmitter<void>();
    @Output() showPersonDetails = new EventEmitter<number>();

    pilots: PersonWithRegisterStatus[];
    crewLookup: { [id: number]: string } = {};
    private ngUnsubscribe$ = new Subject<void>();
    selectPersonnelRegisterStatus = new BehaviorSubject<string>('');
    showRpicRegisterDetails = false;
    authorityIds: number[];

    personSearch = personSearch.bind(this);

    isPilot = false;
    registerEntriesOnMissionLoading = false;
    isPilotRoleSelected = false;
    selectPerson = true;

    hasRpa = false;

    missionCrew: MissionCrewDto;
    private missionCrew$ = new Subject<MissionCrewDto>();
    onChange = Function.prototype;
    onTouched = Function.prototype;

    loadingPersonnel = false;

    constructor(
        @Optional() private missionEditService: MissionEditService,
        private missionPilotRoleDataService: MissionPilotRoleDataService,
        private changeDetector: ChangeDetectorRef,
        private formatPersonPipe: FormatPersonPipe
    ) {}

    ngOnInit() {
        this.missionPilotRoleDataService.working$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => {
                this.loadingPersonnel = working;
                this.changeDetector.markForCheck();
            });

        if (this.missionEditService) {
            this.missionEditService.registerEntriesOnMissionLoading$
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(
                    loading => (this.registerEntriesOnMissionLoading = loading)
                );
            combineLatest([
                this.missionEditService.registerEntriesOnMission$,
                this.missionCrew$,
                this.missionEditService.missionForm.controls.craftIds.valueChanges.pipe(
                    startWith(
                        this.missionEditService.missionForm.controls.craftIds
                            .value as number[]
                    )
                )
            ])
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(([status, crew, rpaIds]) => {
                    this.isPilot =
                        status.find(s => s.entityId === crew?.personId) != null;
                    this.hasRpa = rpaIds != null && rpaIds.length > 0;
                });
        }

        // In read-only mode the assumption is that the user is not a member of the mission's
        // organisation, so personnel list and register-status fetches would most likely
        // 403.  Build the crewLookup directly from the mission crew details so that person
        // names render correctly even without loading the full personnel list.
        if (!this.missionEditService.isReadOnly) {
            this.missionPilotRoleDataService.setParameters(
                this.orgId,
                null,
                null,
                this.missionDetails,
                this.missionCrew$
            );
        } else {
            this.updateCrewLookup();
        }

        this.missionPilotRoleDataService.personnel$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(personnel => {
                this.pilots = personnel;
                this.updateCrewLookup();
            });

        this.missionPilotRoleDataService.authorityIds$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(ids => (this.authorityIds = ids));
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('missionDetails' in changes) {
            this.updateCrewLookup();
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.missionCrew$.complete();
    }

    updateCrewLookup() {
        if (
            this.pilots != null &&
            (<PersonWithRegisterStatus[]>this.pilots).length > 0
        ) {
            this.pilots.forEach(person => {
                if (this.crewLookup[person.id] == null) {
                    this.crewLookup[person.id] =
                        this.formatPersonPipe.transform(person);
                }
            });
        } else {
            this.missionDetails?.missionCrewDetails?.forEach(crew => {
                if (
                    crew.person != null &&
                    this.crewLookup[crew.person.id] == null
                ) {
                    this.crewLookup[crew.person.id] =
                        this.formatPersonPipe.transform(crew.person);
                }
            });
        }
    }

    updateIfPilotRoleSelected() {
        if (this.roles == null) {
            return;
        }
        const role = this.roles.find(
            r => r.id != null && r.id === this.missionCrew.missionRoleId
        );
        this.isPilotRoleSelected = role?.coreRole != null;
    }

    writeValue(value: any): void {
        this.missionCrew = value ?? { personId: null, missionRoleId: null };
        this.selectPerson = this.missionCrew.personId == null;
        this.missionCrew$.next(this.missionCrew);
        this.updateIfPilotRoleSelected();
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    validate(c: FormControl) {
        const isAllValid =
            this.missionCrew != null &&
            this.missionCrew.missionRoleId != null &&
            this.missionCrew.personId != null;
        return isAllValid
            ? null
            : {
                  length: {
                      valid: isAllValid
                  }
              };
    }

    onUpdate() {
        if (this.missionCrew.missionRoleId != null) {
            this.updateIfPilotRoleSelected();
        }
        // If any field updates, move the person to view mode
        if (this.missionCrew.personId != null) {
            this.selectPerson = false;
        }
        this.onChange(this.missionCrew);
    }

    deleteRow() {
        this.deleteCrew.emit();
    }

    getPilotRegisterStatus(personId: number) {
        return this.pilots?.find(p => p.id === personId)?.registerStatuses;
    }

    toggleShowPilotRegisterDetails() {
        this.showRpicRegisterDetails = !this.showRpicRegisterDetails;
        this.toggleShowDetails.emit();
    }

    onPilotSelectScroll() {
        this.missionPilotRoleDataService.onPersonSelectScroll();
    }

    onPilotSelectSearch(search: any) {
        this.missionPilotRoleDataService.onPersonSelectSearch(search);
    }

    onPilotSelectClose() {
        this.missionPilotRoleDataService.onPersonSelectClose();
    }
}
